import { m } from "framer-motion";
import { VoteAnswer } from "../../api/events-api";
import { EASE } from "./Motion";

/**
 * The three answers, in the order they sit on the track — declining on the
 * left through to attending on the right, so the switch reads as a scale
 * rather than an arbitrary row of buttons.
 *
 * Pill colours are the soft tones the roster avatars already use, which both
 * ties the switch to the groups above it and keeps the label dark: white
 * text would be unreadable for the moment it sits on the pale track, before
 * the pill catches up with it.
 */
const SEGMENTS: Array<{ answer: VoteAnswer; label: string; pill: string }> = [
  { answer: VoteAnswer.NO, label: "Afbud", pill: "bg-clay-soft" },
  { answer: VoteAnswer.MAYBE, label: "Måske", pill: "bg-vote-maybe-soft" },
  { answer: VoteAnswer.YES, label: "Deltager", pill: "bg-vote-yes-soft" },
];

/** The track's `p-1.5`, in px — the pill is inset by it on every side. */
const TRACK_PADDING = 6;

/**
 * A three-way switch for one member's attendance: afbud / måske / deltager.
 *
 * The pill slides between slots on a spring with just enough overshoot to
 * feel physical. That only reads as a response to the tap if the answer it
 * animates towards is already the new one, so `value` is expected to be
 * optimistic — the caller moves it on tap and reverts if the write fails,
 * rather than waiting out a round-trip with a frozen switch.
 *
 * `<MotionConfig reducedMotion="user">` in index.tsx drops the travel (and
 * the scale) for anyone who's asked their OS for less motion, leaving the
 * fade, so there's nothing to opt out of by hand here.
 */
export function AttendanceToggle({
  value,
  onChange,
  disabled = false,
}: {
  value: VoteAnswer | undefined;
  onChange: (answer: VoteAnswer) => void;
  /**
   * Renders the switch in place but inert — used where the control is on
   * screen before it has anything to act on (the admin editor, before a
   * member has been picked). Kept mounted rather than hidden so nothing
   * below it moves when a target is chosen.
   */
  disabled?: boolean;
}) {
  const index = SEGMENTS.findIndex((s) => s.answer === value);
  const selected = SEGMENTS[index];
  // A transform percentage resolves against the element's own width, and the
  // pill is exactly one slot wide, so slot n sits at n * 100% — no measuring
  // and nothing to recalculate when the track resizes.
  const slot = `${index * 100}%`;

  return (
    // The hairline is what makes this read as something you can touch: the
    // track alone is barely a shade off the page, which left the one control
    // here looking fainter than the passive vært card above it.
    <div
      className={`relative grid grid-cols-3 bg-paper2 border border-line-soft rounded-lg p-1.5 transition-opacity duration-200 ${
        disabled ? "opacity-45" : ""
      }`}
    >
      {/* Nothing to point at until the member has answered once: the pill
          appears in place on that first tap rather than travelling in from
          "afbud", which would read as an answer they never gave. */}
      {selected && (
        <m.div
          aria-hidden
          className={`absolute top-1.5 bottom-1.5 left-1.5 rounded-md ${selected.pill} transition-colors duration-300`}
          style={{ width: `calc((100% - ${TRACK_PADDING * 2}px) / ${SEGMENTS.length})` }}
          initial={{ x: slot, opacity: 0, scale: 0.86 }}
          animate={{ x: slot, opacity: 1, scale: 1 }}
          transition={{
            // Measured at ~2.6% of a slot's overshoot, settling in ~340ms:
            // sprung enough to read as physical, and gentle enough that the
            // pill never crowds the end of the track — at the outer slots it
            // still leaves half the inset clear at the peak. Softening the
            // damping much below this (30 overshoots by 5%) puts the pill
            // hard against the track's rounded edge on every trip out to
            // afbud or deltager.
            type: "spring",
            stiffness: 460,
            damping: 32,
            mass: 0.85,
            opacity: { duration: 0.18, ease: EASE },
            scale: { type: "spring", stiffness: 500, damping: 24, mass: 0.8 },
          }}
        />
      )}
      {SEGMENTS.map((seg) => (
        <button
          key={seg.answer}
          type="button"
          aria-pressed={value === seg.answer}
          disabled={disabled}
          onClick={() => onChange(seg.answer)}
          // Relative so the labels paint over the pill sliding beneath them.
          className={`relative py-2.5 rounded-md font-display text-[12.5px] font-bold uppercase tracking-tight transition-colors duration-200 ${
            value === seg.answer ? "text-ink" : "text-ink3"
          }`}
        >
          {seg.label}
        </button>
      ))}
    </div>
  );
}
